import { FC } from 'react';

import { Quote } from '__generated__/GQL';
import invoiceImg from 'assets/brand/invoice@2x.png';
import Button from 'components/_core/Button';
import SunshineModal from 'components/_core/SunshineModal';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import logger from 'helpers/logger';

import { useMarkQuoteAsAccepted } from '../../../mutations/useMarkQuoteAsAccepted';
import { useQuoteModals } from '../..';
import locales from './locales';

export type MarkAsAcceptedProps = Pick<
  Quote,
  'clientContactIds' | 'isUserCcedOnEmails' | 'quoteId'
> & { isFromList: boolean };

const MarkAsAccepted: FC<MarkAsAcceptedProps> = ({
  clientContactIds,
  isFromList = true,
  isUserCcedOnEmails,
  quoteId,
}) => {
  const { showToast } = useToast();
  const { hideModal, openModal } = useQuoteModals();
  const [markQuoteAsAccepted, { loading }] = useMarkQuoteAsAccepted(quoteId);

  const onConfirm = async () => {
    try {
      await markQuoteAsAccepted();

      logEvent({
        name: 'Quote Accepted',
        properties: { quoteId },
      });

      openModal({
        name: 'convert-quote-to-invoice',
        props: {
          clientContactIds,
          isFromList,
          isUserCcedOnEmails,
          quoteId,
        },
      });
    } catch (error) {
      logger.error(error);
      showToast({ message: locales.errorMessage, type: 'error' });
    }
  };

  return (
    <SunshineModal isOpen onClose={hideModal}>
      <SunshineModal.Header />

      <SunshineModal.Body>
        <SunshineModal.Body.Image src={invoiceImg} />
        <SunshineModal.Title>{locales.title}</SunshineModal.Title>
      </SunshineModal.Body>

      <SunshineModal.Footer>
        <Button
          isDisabled={loading}
          onClick={hideModal}
          variant="inline-secondary"
        >
          {locales.cancel}
        </Button>

        <Button isLoading={loading} onClick={onConfirm}>
          {locales.confirm}
        </Button>
      </SunshineModal.Footer>
    </SunshineModal>
  );
};

export default MarkAsAccepted;
